import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["parentMenuOpen", "component", "label", "rightIcon", "children", "className", "tabIndex", "ContainerProps"],
      _excluded2 = ["ref"];

/* eslint-disable no-restricted-syntax */
import React, { useState, useRef, useImperativeHandle } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ArrowRight from '@material-ui/icons/ArrowRight';
import clsx from 'clsx';
const TRANSPARENT = 'rgba(0,0,0,0)';
const useMenuItemStyles = makeStyles(theme => ({
  root: props => ({
    backgroundColor: props.open ? theme.palette.action.hover : TRANSPARENT
  }),
  menuItem: {
    backgroundColor: `${theme.palette.background.dark1}`
  }
}));
/**
 * Use as a drop-in replacement for `<MenuItem>` when you need to add cascading
 * menu elements as children to this component.
 */

var _ref = /*#__PURE__*/React.createElement(ArrowRight, null);

export const NestedMenuItem = /*#__PURE__*/React.forwardRef(function NestedMenuItem(props, ref) {
  const {
    parentMenuOpen,
    component = 'div',
    label,
    rightIcon = _ref,
    children,
    className,
    tabIndex: tabIndexProp,
    // MenuProps = {},
    ContainerProps: ContainerPropsProp = {}
  } = props,
        MenuItemProps = _objectWithoutProperties(props, _excluded);

  const {
    ref: containerRefProp
  } = ContainerPropsProp,
        ContainerProps = _objectWithoutProperties(ContainerPropsProp, _excluded2);

  const menuItemRef = useRef(null);
  useImperativeHandle(ref, () => menuItemRef.current);
  const containerRef = useRef(null);
  useImperativeHandle(containerRefProp, () => containerRef.current);
  const menuContainerRef = useRef(null);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

  const handleMouseEnter = event => {
    setIsSubMenuOpen(true);

    if (ContainerProps !== null && ContainerProps !== void 0 && ContainerProps.onMouseEnter) {
      ContainerProps.onMouseEnter(event);
    }
  };

  const handleClickEnter = event => {
    setIsSubMenuOpen(true);

    if (ContainerProps !== null && ContainerProps !== void 0 && ContainerProps.onClick) {
      ContainerProps.onClick(event);
    }
  };

  const handleMouseLeave = event => {
    setIsSubMenuOpen(false);

    if (ContainerProps !== null && ContainerProps !== void 0 && ContainerProps.onMouseLeave) {
      ContainerProps.onMouseLeave(event);
    }
  }; // Check if any immediate children are active


  const isSubmenuFocused = () => {
    var _containerRef$current, _containerRef$current2;

    const active = (_containerRef$current = containerRef.current) === null || _containerRef$current === void 0 ? void 0 : (_containerRef$current2 = _containerRef$current.ownerDocument) === null || _containerRef$current2 === void 0 ? void 0 : _containerRef$current2.activeElement;

    for (const child of (_menuContainerRef$cur = (_menuContainerRef$cur2 = menuContainerRef.current) === null || _menuContainerRef$cur2 === void 0 ? void 0 : _menuContainerRef$cur2.children) !== null && _menuContainerRef$cur !== void 0 ? _menuContainerRef$cur : []) {
      var _menuContainerRef$cur, _menuContainerRef$cur2;

      if (child === active) {
        return true;
      }
    }

    return false;
  };

  const handleFocus = event => {
    if (event.target === containerRef.current) {
      setIsSubMenuOpen(true);
    }

    if (ContainerProps !== null && ContainerProps !== void 0 && ContainerProps.onFocus) {
      ContainerProps.onFocus(event);
    }
  };

  const handleKeyDown = event => {
    var _containerRef$current3, _containerRef$current4;

    if (event.key === 'Escape') {
      return;
    }

    if (isSubmenuFocused()) {
      event.stopPropagation();
    }

    const active = (_containerRef$current3 = containerRef.current) === null || _containerRef$current3 === void 0 ? void 0 : (_containerRef$current4 = _containerRef$current3.ownerDocument) === null || _containerRef$current4 === void 0 ? void 0 : _containerRef$current4.activeElement;

    if (event.key === 'ArrowLeft' && isSubmenuFocused()) {
      var _containerRef$current5;

      // eslint-disable-next-line no-unused-expressions
      (_containerRef$current5 = containerRef.current) === null || _containerRef$current5 === void 0 ? void 0 : _containerRef$current5.focus();
    }

    if (event.key === 'ArrowRight' && event.target === containerRef.current && event.target === active) {
      var _menuContainerRef$cur3;

      const firstChild = (_menuContainerRef$cur3 = menuContainerRef.current) === null || _menuContainerRef$cur3 === void 0 ? void 0 : _menuContainerRef$cur3.children[0]; // eslint-disable-next-line no-unused-expressions

      firstChild === null || firstChild === void 0 ? void 0 : firstChild.focus();
    }
  };

  const open = isSubMenuOpen && parentMenuOpen;
  const menuItemClasses = useMenuItemStyles({
    open
  }); // Root element must have a `tabIndex` attribute for keyboard navigation

  let tabIndex;

  if (!props.disabled) {
    tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1;
  }

  return /*#__PURE__*/React.createElement("div", _extends({}, ContainerProps, {
    ref: containerRef,
    onFocus: handleFocus,
    tabIndex: tabIndex,
    onMouseEnter: handleMouseEnter,
    onClick: handleClickEnter,
    onMouseLeave: handleMouseLeave,
    onKeyDown: handleKeyDown,
    role: "button"
  }), /*#__PURE__*/React.createElement(MenuItem, _extends({}, MenuItemProps, {
    className: clsx(menuItemClasses.root, className),
    ref: menuItemRef
  }), label, rightIcon), /*#__PURE__*/React.createElement(Menu // Set pointer events to 'none' to prevent the invisible Popover div
  // from capturing events for clicks and hovers
  , {
    style: {
      pointerEvents: 'none'
    },
    anchorEl: menuItemRef.current,
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right'
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left'
    },
    open: open,
    autoFocus: false,
    disableAutoFocus: true,
    disableEnforceFocus: true,
    onClose: () => {
      setIsSubMenuOpen(false);
    },
    PaperProps: {
      classes: {
        elevation8: menuItemClasses.menuItem
      }
    }
  }, /*#__PURE__*/React.createElement("div", {
    ref: menuContainerRef,
    style: {
      pointerEvents: 'auto'
    }
  }, children)));
});
export default NestedMenuItem;